<template>
  <section id="related-integrations" class="lg:m-[auto]">
    <div class="flex flex-col gap-y-4 text-center items-center">
      <span class="text-xs font-semibold text-lighter uppercase tracking-widest">{{ t('main.integration.related.eyebrow') }}</span>
      <h2 class="text-2xl lg:text-3xl font-bold font-markPro text-dark tracking-[-0.05rem]">
        {{ t('main.integration.related.title') }}
      </h2>
      <p class="text-lighter font-semibold max-w-[500px]">
        {{ t('main.integration.related.description') }}
      </p>
    </div>

    <div class="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4 w-full">
      <RouterLink
        v-for="item in items"
        :key="item.slug"
        :to="{ name: item.routeName }"
        class="group bg-white rounded-2xl drop-shadow overflow-hidden flex flex-col hover:drop-shadow-lg transition-all duration-300"
      >
        <div class="relative overflow-hidden">
          <img
            :src="publicPath(`/images/integrations/${item.image}.webp`)"
            :alt="item.name"
            class="w-full h-auto transition-transform duration-300 group-hover:scale-105"
          />
        </div>
        <div class="p-3 flex items-center justify-between">
          <div>
            <p class="font-bold text-dark text-sm font-markPro">{{ item.name }}</p>
            <p class="text-lighter text-xs font-semibold">{{ t('pages.info.integrations_detail.' + item.tk + '.hero.category') }}</p>
          </div>
          <i class="bi bi-arrow-right text-lighter group-hover:text-dark group-hover:translate-x-0.5 transition-all duration-200 shrink-0"></i>
        </div>
      </RouterLink>
    </div>
  </section>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { publicPath } from '@/utils/publicPath.ts'

const { t } = useI18n()

const props = defineProps<{
  exclude: string
}>()

const ALL_INTEGRATIONS = [
  { slug: 'lightspeed', name: 'Lightspeed', tk: 'lightspeed', routeName: 'integrations.lightspeed', image: 'lightspeed' },
  { slug: 'mollie', name: 'Mollie', tk: 'mollie', routeName: 'integrations.mollie', image: 'mollie' },
  { slug: 'stripe', name: 'Stripe', tk: 'stripe', routeName: 'integrations.stripe', image: 'stripe' },
  { slug: 'exact', name: 'Exact Online', tk: 'exactonline', routeName: 'integrations.exact', image: 'exact' },
  { slug: 'google', name: 'Google', tk: 'google', routeName: 'integrations.google', image: 'google' },
  { slug: 'winstonpos', name: 'WinstonPOS', tk: 'winstonpos', routeName: 'integrations.winstonpos', image: 'winstonpos' },
  { slug: 'untill', name: 'unTill', tk: 'untill', routeName: 'integrations.untill', image: 'untill' },
  { slug: 'mpluskassa', name: 'M+ Kassa', tk: 'mpluskassa', routeName: 'integrations.mpluskassa', image: 'mpluskassa' },
  { slug: 'twilio', name: 'Twilio', tk: 'twilio', routeName: 'integrations.twilio', image: 'twilio' },
  { slug: 'facebook', name: 'Facebook', tk: 'facebook', routeName: 'integrations.facebook', image: 'facebook' },
  { slug: 'cashdesk', name: 'Cashdesk', tk: 'cashdesk', routeName: 'integrations.cashdesk', image: 'cashdesk' },
  { slug: 'leat', name: 'LEAT', tk: 'leat', routeName: 'integrations.leat', image: 'leat' },
  { slug: 'instagram', name: 'Instagram', tk: 'instagram', routeName: 'integrations.instagram', image: 'instagram' },
  { slug: 'slack', name: 'Slack', tk: 'slack', routeName: 'integrations.slack', image: 'slack' },
]

// Related map: hand-picked per integration for relevance
const RELATED_MAP: Record<string, string[]> = {
  lightspeed: ['mollie', 'stripe', 'untill', 'winstonpos', 'exact'],
  mollie: ['stripe', 'lightspeed', 'exact', 'leat', 'untill'],
  stripe: ['mollie', 'lightspeed', 'exact', 'leat', 'google'],
  exact: ['mollie', 'stripe', 'lightspeed', 'untill', 'mpluskassa'],
  google: ['facebook', 'instagram', 'slack', 'twilio', 'mollie'],
  winstonpos: ['lightspeed', 'untill', 'mpluskassa', 'cashdesk', 'mollie'],
  untill: ['lightspeed', 'winstonpos', 'mpluskassa', 'cashdesk', 'mollie'],
  mpluskassa: ['lightspeed', 'untill', 'winstonpos', 'cashdesk', 'mollie'],
  twilio: ['slack', 'facebook', 'instagram', 'google', 'mollie'],
  facebook: ['instagram', 'google', 'slack', 'twilio', 'mollie'],
  cashdesk: ['lightspeed', 'untill', 'winstonpos', 'mpluskassa', 'mollie'],
  leat: ['mollie', 'stripe', 'lightspeed', 'google', 'twilio'],
  instagram: ['facebook', 'google', 'slack', 'twilio', 'mollie'],
  slack: ['twilio', 'facebook', 'instagram', 'google', 'lightspeed'],
}

const items = computed(() => {
  const related = RELATED_MAP[props.exclude] ?? []
  return ALL_INTEGRATIONS.filter(i => related.includes(i.slug))
})
</script>

<style scoped>
@reference "@/assets/css/main.css";
</style>
